import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  previousReportingPeriod,
  type ReportingPeriodCompare,
  ReportingPeriodSelect,
} from "@/components/patterns/reporting-period-select";
import { ReportingTimeZoneSelect } from "@/components/patterns/reporting-time-zone-select";
import type { ReportingTimeZone } from "@/lib/reporting-time-zone";
import { cn } from "@/lib/utils";
import type { ReportingPeriod } from "@/stores/reporting-period";
import { AsinFilter } from "./asin-filter";
import type { DashboardCompareMode } from "./kpi-strip-metrics";

/** Menu modes; "previous_year" is shown but not offered until the API returns it. */
type CompareOption = DashboardCompareMode | "previous_year";

export interface DashboardFilterBarProps {
  /** Last complete reporting date (YYYY-MM-DD) across the loaded sources. */
  dataThrough: string | null;
  /** Most recent successful sync (ISO datetime), shown in the reporting time zone. */
  syncedAt: string | null;
  /** A sync is queued or running for any shop in scope. */
  syncing?: boolean;
  /** The freshness chip opens the data details in Settings. */
  linkToDataDetails?: boolean;
  period: ReportingPeriod;
  onPeriodChange: (period: ReportingPeriod) => void;
  timeZone: ReportingTimeZone;
  onTimeZoneChange: (timeZone: ReportingTimeZone) => void;
  compare: DashboardCompareMode;
  onCompareChange: (compare: DashboardCompareMode) => void;
  /** Applied child ASINs of the ASIN filter; empty means every product. */
  asins: string[];
  onAsinsChange: (asins: string[]) => void;
  /** Scope caveats (approximate currency, excluded shops) shown beside the freshness chip. */
  notices?: React.ReactNode;
  className?: string;
}

/**
 * Dashboard filter bar: data freshness on the left; ASIN filter, date range (which also
 * owns the comparison period) and reporting time zone on the right.
 */
export function DashboardFilterBar({
  dataThrough,
  syncedAt,
  syncing = false,
  linkToDataDetails = false,
  period,
  onPeriodChange,
  timeZone,
  onTimeZoneChange,
  compare,
  onCompareChange,
  asins,
  onAsinsChange,
  notices,
  className,
}: DashboardFilterBarProps): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const compareOptions: { value: CompareOption; label: string; disabled?: boolean }[] = [
    { value: "off", label: t("overview.filters.compare.off") },
    { value: "previous_period", label: t("overview.filters.compare.previousPeriod") },
    { value: "previous_year", label: t("overview.filters.compare.previousYear"), disabled: true },
  ];
  const setCompare = (next: CompareOption) => {
    if (next !== "previous_year") onCompareChange(next);
  };
  const periodCompare: ReportingPeriodCompare<CompareOption> = {
    value: compare,
    onValueChange: setCompare,
    options: compareOptions,
    label: t("overview.filters.compare.label"),
    resolveRange: (range, mode) =>
      mode === "previous_period" ? previousReportingPeriod(range) : null,
    describe: (range) => t("overview.filters.compare.vs", { range }),
  };

  return (
    <div
      data-slot="dashboard-filter-bar"
      className={cn(
        "flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-2",
        className,
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <FreshnessChip
          dataThrough={dataThrough}
          syncedAt={syncedAt}
          syncing={syncing}
          timeZone={timeZone}
          linkToDataDetails={linkToDataDetails}
        />
        {notices}
      </div>
      <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
        <AsinFilter value={asins} onValueChange={onAsinsChange} />
        <span aria-hidden className="h-5 w-px shrink-0 bg-line" />
        <ReportingPeriodSelect<CompareOption>
          variant="toolbar"
          value={period}
          timeZone={timeZone}
          onValueChange={onPeriodChange}
          compare={periodCompare}
          customPresetLabel={t("overview.filters.customRange")}
          align="end"
        />
        <ReportingTimeZoneSelect value={timeZone} onValueChange={onTimeZoneChange} />
      </div>
    </div>
  );
}

function FreshnessChip({
  dataThrough,
  syncedAt,
  syncing,
  timeZone,
  linkToDataDetails,
}: {
  dataThrough: string | null;
  syncedAt: string | null;
  syncing: boolean;
  timeZone: ReportingTimeZone;
  linkToDataDetails: boolean;
}): React.ReactElement {
  const { t, i18n } = useTranslation("dashboard");
  const language = i18n.resolvedLanguage ?? "en-US";
  const date = dataThrough
    ? new Intl.DateTimeFormat(language, { month: "short", day: "numeric", timeZone: "UTC" }).format(
        new Date(`${dataThrough}T00:00:00.000Z`),
      )
    : null;
  const time = syncedAt
    ? new Intl.DateTimeFormat(language, {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
        timeZone,
      }).format(new Date(syncedAt))
    : null;
  const text = syncing
    ? date
      ? t("overview.filters.freshness.syncingWithData", { date })
      : t("overview.filters.freshness.syncing")
    : !date
      ? t("overview.filters.freshness.noData")
      : time
        ? t("overview.filters.freshness.dataThroughSynced", { date, time })
        : t("overview.filters.freshness.dataThrough", { date });
  const dot = syncing ? "bg-info" : date ? "bg-success" : "bg-placeholder";

  const className =
    "flex h-[22px] max-w-full shrink-0 items-center gap-1.5 rounded-full bg-subtle pr-2 pl-[7px]";
  const content = (
    <>
      <span aria-hidden className={cn("size-1.5 shrink-0 rounded-[3px]", dot)} />
      <span className="truncate text-badge tabular-nums text-text-secondary">{text}</span>
    </>
  );

  return linkToDataDetails ? (
    <Link
      to="/settings"
      search={{ tab: "amazon", dataDetails: true }}
      className={cn(
        className,
        "outline-none transition-colors duration-[var(--duration-fast)] hover:bg-control-subtle-hover focus-visible:ring-2 focus-visible:ring-focus-ring",
      )}
    >
      {content}
    </Link>
  ) : (
    <div role="status" className={className}>
      {content}
    </div>
  );
}
